import React from 'react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import { Button } from '../components/ui/button';
import { 
  Cpu, 
  FileText, 
  Users, 
  Store, 
  BarChart3, 
  Layers, 
  Building2,
  Zap,
  Palette,
  Shield,
  LineChart,
  ArrowRight
} from 'lucide-react';
import CalendlyPopup from '../components/CalendlyPopup';
import { useCalendlyAndVideo } from '../hooks/useCalendlyAndVideo';

const FeaturesPage = () => {
  const { isCalendlyOpen, openCalendly, closeCalendly } = useCalendlyAndVideo();

  const coreFeatures = [
    {
      id: 'navigator-ai',
      icon: Cpu,
      title: 'Navigator AI Engine',
      tagline: 'The intelligence behind Traain.',
      bullets: [
        'Converts existing content into structured training',
        'Builds learning paths automatically',
        'Powers role-play, scoring, and feedback',
        'Adapts to roles, teams, and skill levels'
      ]
    },
    {
      id: 'training-builder',
      icon: FileText,
      title: 'AI Training Builder',
      tagline: 'Create or convert training in minutes.',
      bullets: [
        'Import SOPs, PDFs, slides, and documentation',
        'Instantly generate organized training programs',
        'No instructional design required',
        'Consistent training across teams'
      ]
    },
    {
      id: 'roleplay',
      icon: Users,
      title: 'AI Role-Play & Simulations',
      tagline: 'Practice before it counts.',
      bullets: [
        'Realistic AI-driven scenarios',
        'Sales, service, leadership, and compliance use cases',
        'Performance scoring with instant feedback',
        'Safe environment for skill development'
      ]
    },
    {
      id: 'marketplace',
      icon: Store,
      title: 'Training Marketplace',
      tagline: 'Professional training, ready to deploy.',
      bullets: [
        'Curated, expert-built training programs',
        'Leadership, customer service, sales, compliance',
        'Plug-and-play or customize to your brand',
        'Continuously expanding content library'
      ]
    },
    {
      id: 'performance',
      icon: BarChart3,
      title: 'Performance Scoring & Coaching',
      tagline: 'Training you can measure.',
      bullets: [
        'Automated scoring and feedback',
        'Individual and team-level performance insights',
        'Skill progression tracking over time',
        'Less manual coaching, better outcomes'
      ]
    },
    {
      id: 'enterprise-lms',
      icon: Layers,
      title: 'Enterprise LMS Replacement',
      tagline: 'Modern learning without legacy friction.',
      bullets: [
        'Replace static LMS platforms',
        'Migrate and enhance existing training',
        'Dynamic, AI-powered learning experiences',
        'Built to scale across teams and locations'
      ]
    },
    {
      id: 'multi-team',
      icon: Building2,
      title: 'Multi-Team & Multi-Location Training',
      tagline: 'Train once. Scale everywhere.',
      bullets: [
        'Role-based training paths',
        'Consistent learning across departments',
        'Centralized visibility for leadership',
        'Easy assignment and tracking'
      ]
    }
  ];

  const platformCapabilities = [
    { id: 'fast-setup', icon: Zap, text: 'Fast setup and onboarding' },
    { id: 'branding', icon: Palette, text: 'Custom branding and language' },
    { id: 'security', icon: Shield, text: 'Secure, enterprise-ready infrastructure' },
    { id: 'analytics', icon: LineChart, text: 'Actionable analytics and insights' }
  ];

  return (
    <div className="min-h-screen bg-gray-950 text-white">
      <SEO 
        title="Features | Traain Platform Overview"
        description="Everything you need to train teams, build real skills, and drive performance—powered by Navigator AI."
        keywords="AI training, role-play simulations, LMS replacement, training marketplace, Navigator AI"
      />
      
      {/* Animated Background */}
      <div className="fixed inset-0 z-0">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-950 via-gray-900 to-indigo-950 animate-gradient" />
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-0 left-0 w-96 h-96 bg-cyan-500 rounded-full filter blur-3xl animate-blob" />
          <div className="absolute top-0 right-0 w-96 h-96 bg-blue-500 rounded-full filter blur-3xl animate-blob animation-delay-2000" />
          <div className="absolute bottom-0 left-1/2 w-96 h-96 bg-indigo-500 rounded-full filter blur-3xl animate-blob animation-delay-4000" />
        </div>
      </div>

      {/* Content */}
      <div className="relative z-10">
        <Navbar />
        
        {/* Hero Section */}
        <section className="pt-32 pb-20 px-6">
          <div className="max-w-5xl mx-auto text-center">
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold mb-6 text-white">
              Features <span className="text-cyan-400">Overview</span>
            </h1>
            
            <p className="text-xl md:text-2xl text-gray-300 mb-12 max-w-4xl mx-auto leading-relaxed">
              Everything you need to train teams, build real skills, and drive performance—powered by Navigator AI.
            </p>

            <Button
              size="lg"
              onClick={openCalendly}
              className="h-14 bg-cyan-500 text-black hover:bg-cyan-400 rounded-lg px-8 text-lg font-semibold transition-all duration-200 hover:scale-105 hover:shadow-lg hover:shadow-cyan-500/50"
            >
              Request a Demo
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </div>
        </section>

        {/* Core Features Section */}
        <section className="py-20 px-6">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold mb-4 text-white">
                Core <span className="text-cyan-400">Features</span>
              </h2>
            </div>

            <div className="space-y-8">
              {coreFeatures.map((feature) => {
                const Icon = feature.icon;
                return (
                  <div
                    key={feature.id}
                    className="p-8 md:p-10 rounded-3xl bg-gradient-to-br from-gray-900/90 to-blue-900/30 border border-cyan-500/30 hover:border-cyan-400/50 transition-all duration-300"
                  >
                    <div className="flex flex-col md:flex-row md:items-start gap-6">
                      <div className="w-16 h-16 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-2xl flex items-center justify-center shadow-lg flex-shrink-0">
                        <Icon className="w-8 h-8 text-white" />
                      </div>
                      
                      <div className="flex-1">
                        <h3 className="text-2xl md:text-3xl font-bold text-white mb-2">
                          {feature.title}
                        </h3>
                        <p className="text-lg text-cyan-400 font-semibold mb-6">
                          {feature.tagline}
                        </p>
                        
                        <div className="grid md:grid-cols-2 gap-3">
                          {feature.bullets.map((bullet, idx) => (
                            <div key={`${feature.id}-bullet-${idx}`} className="flex items-start gap-3">
                              <div className="w-2 h-2 bg-cyan-400 rounded-full mt-2 flex-shrink-0" />
                              <span className="text-gray-300">{bullet}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Platform Capabilities Section */}
        <section className="py-20 px-6 bg-gradient-to-b from-transparent to-blue-950/20">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl md:text-5xl font-bold mb-4 text-white">
                Platform <span className="text-cyan-400">Capabilities</span>
              </h2>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {platformCapabilities.map((capability) => {
                const Icon = capability.icon;
                return (
                  <div
                    key={capability.id}
                    className="p-6 rounded-2xl bg-gradient-to-br from-gray-900/90 to-blue-900/30 border border-cyan-500/30 hover:border-cyan-400/50 transition-all duration-300 text-center"
                  >
                    <div className="w-14 h-14 bg-gradient-to-br from-cyan-500/20 to-blue-500/20 rounded-xl flex items-center justify-center mx-auto mb-4">
                      <Icon className="w-7 h-7 text-cyan-400" />
                    </div>
                    <p className="text-gray-300 font-medium">{capability.text}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 px-6">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl md:text-5xl font-bold mb-6 text-white">
              Ready to Transform Your <span className="text-cyan-400">Training?</span>
            </h2>
            
            <p className="text-xl text-gray-300 mb-12">
              See how Traain can help your teams perform better, faster.
            </p>

            <Button
              size="lg"
              onClick={openCalendly}
              className="h-14 bg-cyan-500 text-black hover:bg-cyan-400 rounded-lg px-8 text-lg font-semibold transition-all duration-200 hover:scale-105 hover:shadow-lg hover:shadow-cyan-500/50"
            >
              Schedule a Discovery Call
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </div>
        </section>

        <Footer />
      </div>
      
      <CalendlyPopup isOpen={isCalendlyOpen} onClose={closeCalendly} />
    </div>
  );
};

export default FeaturesPage;
